// Copyright 2026 Rabot Inc.
//
//    Licensed under the Apache License, Version 2.0 (the "License");
//    you may not use this file except in compliance with the License.
//    You may obtain a copy of the License at
//
//        http://www.apache.org/licenses/LICENSE-2.0
//
//    Unless required by applicable law or agreed to in writing, software
//    distributed under the License is distributed on an "AS IS" BASIS,
//    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//    See the License for the specific language governing permissions and
//    limitations under the License.
import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Button, CircularProgress } from '@mui/material';

import { signInWithPopup } from 'firebase/auth';

import { setSnackbar } from '../../actions/appActions';
import { loginWithFirebase } from '../../actions/userActions';
import { auth, googleProvider, isFirebaseConfigured } from '../../firebase/config';

// Multicolor Google "G" mark, matching the rabot-apollo login button.
const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

export const GoogleSignInButton = ({ className = '' }) => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const handleGoogleSignIn = useCallback(async () => {
    if (!isFirebaseConfigured()) {
      dispatch(setSnackbar('Google Sign-In is not configured'));
      return;
    }

    setIsLoading(true);
    try {
      // Sign in with Google via Firebase
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Get the Firebase ID token
      const idToken = await user.getIdToken();

      // Send the token to our backend to exchange for a Mender JWT
      await dispatch(loginWithFirebase(idToken));
    } catch (error) {
      console.error('Google Sign-In error:', error);
      let errorMessage = 'Sign in failed. Please try again.';

      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign-in popup was closed';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Sign-in popup was blocked. Please allow popups for this site.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your connection.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      dispatch(setSnackbar(errorMessage));
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  if (!isFirebaseConfigured()) {
    return null;
  }

  return (
    <Button
      className={`google-signin-button ${className}`}
      variant="outlined"
      onClick={handleGoogleSignIn}
      disabled={isLoading}
      startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <GoogleIcon />}
      fullWidth
      sx={{
        textTransform: 'none',
        fontFamily: '"Inter", system-ui, -apple-system, sans-serif',
        fontWeight: 600,
        fontSize: '0.875rem',
        padding: '14px 24px',
        gap: 1,
        color: 'text.primary',
        borderColor: 'divider',
        '&:hover': {
          borderColor: 'divider',
          backgroundColor: 'action.hover'
        }
      }}
    >
      {isLoading ? 'Signing in...' : 'Continue with Google'}
    </Button>
  );
};

export default GoogleSignInButton;
